import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Avatar from "@/components/chat/Avatar";
import {
  Users, Film, BarChart3, Megaphone, Search, ShieldOff, ShieldCheck,
  Trash2, X, ArrowLeft, RefreshCw, AlertTriangle, Ban, MessageSquare,
} from "lucide-react";

/**
 * Hidden admin panel. The panel is reachable only via direct URL by users
 * whose `is_admin` flag is true (backend enforces this). Any non-admin
 * visitor is bounced to /app/chats.
 *
 * The panel exposes: stats overview, user management (block/unblock/delete),
 * reel moderation and a broadcast toast to every online user.
 */
const TABS = [
  { key: "stats", label: "İstatistikler", icon: BarChart3 },
  { key: "users", label: "Kullanıcılar", icon: Users },
  { key: "messages", label: "Mesajlar", icon: MessageSquare },
  { key: "reels", label: "Reels", icon: Film },
  { key: "broadcast", label: "Duyuru", icon: Megaphone },
];

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) {
      navigate("/app/chats", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || !user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="font-display text-xl">Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#F9F8F6] text-[#0A0A0A]" data-testid="admin-page">
      <header className="bg-white border-b-[3px] border-[#0A0A0A] px-4 md:px-8 py-4 flex items-center gap-3 sticky top-0 z-20">
        <Link
          to="/app/chats"
          data-testid="admin-back-btn"
          className="w-10 h-10 border-hard rounded-xl bg-white flex items-center justify-center press-btn shadow-hard-sm"
          aria-label="Geri"
        >
          <ArrowLeft strokeWidth={3} className="w-4 h-4" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase font-black tracking-widest text-[#555]">Yönetim Paneli</div>
          <div className="font-display text-2xl md:text-3xl leading-tight">Yappo · Admin</div>
        </div>
      </header>

      <nav className="bg-white border-b-[3px] border-[#0A0A0A] px-2 md:px-8 overflow-x-auto">
        <div className="flex gap-1.5 py-2 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border-[2.5px] border-[#0A0A0A] text-sm font-black uppercase tracking-tight press-btn shadow-hard-sm ${active ? "bg-[#CCFF00]" : "bg-white"}`}
              >
                <Icon strokeWidth={3} className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="p-4 md:p-8 max-w-6xl mx-auto">
        {tab === "stats" && <StatsPanel />}
        {tab === "users" && <UsersPanel currentUserId={user.id} />}
        {tab === "messages" && <MessagesPanel />}
        {tab === "reels" && <ReelsPanel />}
        {tab === "broadcast" && <BroadcastPanel />}
      </main>
    </div>
  );
}

function StatCard({ label, value, tone = "default", testid }) {
  const bg =
    tone === "danger" ? "#FFD0C2" :
    tone === "success" ? "#CCFF00" :
    tone === "warn" ? "#FFF7C2" :
    "#FFFFFF";
  return (
    <div
      data-testid={testid}
      className="border-hard shadow-hard rounded-2xl p-4 md:p-5"
      style={{ background: bg }}
    >
      <div className="text-[10px] uppercase font-black tracking-widest text-[#333]">{label}</div>
      <div className="font-display text-3xl md:text-4xl mt-1">{value}</div>
    </div>
  );
}

function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="admin-stats-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-2xl">Genel bakış</div>
          <div className="text-sm text-[#555] font-medium">Uygulamanın anlık durumu</div>
        </div>
        <button
          data-testid="admin-stats-refresh"
          onClick={load}
          disabled={loading}
          className="w-10 h-10 border-hard rounded-xl bg-white flex items-center justify-center press-btn shadow-hard-sm disabled:opacity-50"
          aria-label="Yenile"
        >
          <RefreshCw strokeWidth={3} className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {err && <div className="border-hard-2 rounded-xl bg-[#FF9E80] p-3 text-sm font-bold">{err}</div>}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard testid="stat-total-users" label="Toplam kullanıcı" value={stats.total_users} />
          <StatCard testid="stat-online-now" label="Şu an çevrimiçi" value={stats.online_now} tone="success" />
          <StatCard testid="stat-active-day" label="Bugün aktif" value={stats.active_day} />
          <StatCard testid="stat-active-week" label="Bu hafta aktif" value={stats.active_week} />
          <StatCard testid="stat-total-messages" label="Toplam mesaj" value={stats.total_messages} />
          <StatCard testid="stat-messages-day" label="Bugün mesaj" value={stats.messages_day} />
          <StatCard testid="stat-total-conv" label="Sohbet sayısı" value={stats.total_conversations} />
          <StatCard testid="stat-total-reels" label="Reels sayısı" value={stats.total_reels} />
          <StatCard testid="stat-blocked-users" label="Engelli kullanıcı" value={stats.blocked_users} tone={stats.blocked_users > 0 ? "danger" : "default"} />
        </div>
      )}
    </section>
  );
}

function UsersPanel({ currentUserId }) {
  const [q, setQ] = useState("");
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type:'block'|'delete', user}

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (onlyBlocked) params.set("only_blocked", "true");
      params.set("limit", "100");
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, onlyBlocked]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const doBlock = async (u, reason) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/block`, { reason });
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const doUnblock = async (u) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/unblock`, {});
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (u) => {
    setBusyId(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  return (
    <section data-testid="admin-users-panel" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-display text-2xl">Kullanıcılar</div>
          <div className="text-sm text-[#555] font-medium">{total} kullanıcı listeleniyor</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-black uppercase tracking-widest border-hard-2 rounded-xl px-3 py-2 bg-white cursor-pointer press-btn shadow-hard-sm">
            <input
              type="checkbox"
              data-testid="admin-users-only-blocked"
              checked={onlyBlocked}
              onChange={(e) => setOnlyBlocked(e.target.checked)}
            />
            Sadece engelliler
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-white border-hard rounded-xl px-3 py-2 shadow-hard-sm">
        <Search strokeWidth={2.5} className="w-4 h-4" />
        <input
          data-testid="admin-users-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="E-posta veya isme göre ara…"
          className="flex-1 bg-transparent focus:outline-none text-sm"
        />
      </div>

      {err && <div className="border-hard-2 rounded-xl bg-[#FF9E80] p-3 text-sm font-bold">{err}</div>}

      <div className="grid gap-2" data-testid="admin-users-list">
        {loading && <div className="text-center text-[#555] py-8">Yükleniyor…</div>}
        {!loading && users.length === 0 && <div className="text-center text-[#555] py-8">Kullanıcı bulunamadı.</div>}
        {users.map((u) => (
          <div
            key={u.id}
            data-testid={`admin-user-row-${u.id}`}
            className={`border-hard-2 rounded-2xl p-3 md:p-4 shadow-hard-sm flex items-center gap-3 ${u.is_blocked ? "bg-[#FFE7CC]" : "bg-white"}`}
          >
            <Avatar name={u.name} picture={u.picture} size={44} online={u.online} showStatus />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-black truncate">{u.name}</div>
                {u.is_admin && (
                  <span className="text-[9px] uppercase font-black tracking-widest bg-[#CCFF00] border-[2px] border-[#0A0A0A] rounded-full px-1.5 py-0.5">Admin</span>
                )}
                {u.is_blocked && (
                  <span className="text-[9px] uppercase font-black tracking-widest bg-[#FF3B30] text-white border-[2px] border-[#0A0A0A] rounded-full px-1.5 py-0.5">Engelli</span>
                )}
              </div>
              <div className="text-xs text-[#555] truncate">{u.email}</div>
              {u.is_blocked && u.blocked_reason && (
                <div className="text-xs mt-0.5 font-bold text-[#B23A00] truncate">Sebep: {u.blocked_reason}</div>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {u.is_blocked ? (
                <button
                  data-testid={`admin-unblock-${u.id}`}
                  onClick={() => doUnblock(u)}
                  disabled={busyId === u.id}
                  className="h-9 px-3 border-hard-2 rounded-lg bg-[#CCFF00] flex items-center gap-1.5 text-xs font-black uppercase press-btn shadow-hard-sm disabled:opacity-50"
                >
                  <ShieldCheck strokeWidth={3} className="w-3.5 h-3.5" />
                  Aç
                </button>
              ) : (
                <button
                  data-testid={`admin-block-${u.id}`}
                  onClick={() => setConfirm({ type: "block", user: u })}
                  disabled={busyId === u.id || u.id === currentUserId}
                  className="h-9 px-3 border-hard-2 rounded-lg bg-white flex items-center gap-1.5 text-xs font-black uppercase press-btn shadow-hard-sm disabled:opacity-50"
                >
                  <ShieldOff strokeWidth={3} className="w-3.5 h-3.5" />
                  Engelle
                </button>
              )}
              <button
                data-testid={`admin-delete-${u.id}`}
                onClick={() => setConfirm({ type: "delete", user: u })}
                disabled={busyId === u.id || u.id === currentUserId}
                className="h-9 w-9 border-hard-2 rounded-lg bg-[#FF9E80] flex items-center justify-center press-btn shadow-hard-sm disabled:opacity-50"
                aria-label="Sil"
                title="Kullanıcıyı sil"
              >
                <Trash2 strokeWidth={3} className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {confirm && (
        <ConfirmDialog
          confirm={confirm}
          onCancel={() => setConfirm(null)}
          onBlock={(reason) => doBlock(confirm.user, reason)}
          onDelete={() => doDelete(confirm.user)}
        />
      )}
    </section>
  );
}

function ConfirmDialog({ confirm, onCancel, onBlock, onDelete }) {
  const [reason, setReason] = useState("");
  const isBlock = confirm.type === "block";
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="admin-confirm-dialog"
    >
      <div
        className="w-full max-w-md bg-white border-hard shadow-hard rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl border-hard-2 flex items-center justify-center ${isBlock ? "bg-[#FFF7C2]" : "bg-[#FF9E80]"}`}>
            {isBlock ? <Ban strokeWidth={3} className="w-5 h-5" /> : <AlertTriangle strokeWidth={3} className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <div className="font-display text-xl leading-tight">
              {isBlock ? "Kullanıcıyı engelle" : "Kullanıcıyı sil"}
            </div>
            <div className="text-sm text-[#555] mt-1">
              <b>{confirm.user.name}</b> — {confirm.user.email}
            </div>
            {isBlock ? (
              <>
                <p className="text-xs text-[#333] mt-3">Bu işlemden sonra kullanıcı giriş yapamayacak, açık oturumları kapatılacak.</p>
                <input
                  data-testid="admin-block-reason-input"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={200}
                  placeholder="Sebep (opsiyonel — kullanıcıya gösterilir)"
                  className="mt-3 w-full bg-[#F9F8F6] border-hard rounded-xl px-3 py-2 text-sm focus:outline-none focus:bg-white"
                />
              </>
            ) : (
              <p className="text-xs text-[#333] mt-3">
                <b>Geri alınamaz.</b> Kullanıcı, tüm mesajları, arkadaşlıkları ve reels&apos;leri silinecek.
              </p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 border-hard-2 rounded-lg bg-white flex items-center justify-center press-btn"
            aria-label="Kapat"
            data-testid="admin-confirm-cancel"
          >
            <X strokeWidth={3} className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            data-testid="admin-confirm-cancel-btn"
            onClick={onCancel}
            className="flex-1 h-11 border-hard-2 rounded-xl bg-white font-black text-sm press-btn shadow-hard-sm"
          >
            Vazgeç
          </button>
          <button
            data-testid="admin-confirm-do"
            onClick={() => (isBlock ? onBlock(reason) : onDelete())}
            className={`flex-1 h-11 border-hard rounded-xl font-black text-sm press-btn shadow-hard text-white ${isBlock ? "bg-[#0A0A0A]" : "bg-[#FF3B30]"}`}
          >
            {isBlock ? "Engelle" : "Sil"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReelsPanel() {
  const [reels, setReels] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/reels?limit=100");
      setReels(data.reels || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doDelete = async (r) => {
    if (!window.confirm("Bu reel'i silmek istediğine emin misin?")) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/reels/${r.id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section data-testid="admin-reels-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-2xl">Reels moderasyonu</div>
          <div className="text-sm text-[#555] font-medium">{total} reel</div>
        </div>
        <button
          data-testid="admin-reels-refresh"
          onClick={load}
          className="w-10 h-10 border-hard rounded-xl bg-white flex items-center justify-center press-btn shadow-hard-sm"
          aria-label="Yenile"
        >
          <RefreshCw strokeWidth={3} className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {err && <div className="border-hard-2 rounded-xl bg-[#FF9E80] p-3 text-sm font-bold">{err}</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-reels-list">
        {loading && <div className="col-span-full text-center text-[#555] py-8">Yükleniyor…</div>}
        {!loading && reels.length === 0 && <div className="col-span-full text-center text-[#555] py-8">Hiç reel yok.</div>}
        {reels.map((r) => (
          <div
            key={r.id}
            data-testid={`admin-reel-${r.id}`}
            className="border-hard-2 rounded-2xl overflow-hidden bg-white shadow-hard-sm"
          >
            <div className="aspect-[9/16] bg-[#0A0A0A] relative overflow-hidden">
              {r.thumbnail_url ? (
                <img src={r.thumbnail_url} alt="" className="w-full h-full object-cover" />
              ) : r.media_url ? (
                <video src={r.media_url} className="w-full h-full object-cover" muted playsInline />
              ) : null}
              <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 to-transparent text-white">
                <div className="text-[10px] font-black">{r.author?.name}</div>
                {r.caption && <div className="text-[10px] truncate opacity-90">{r.caption}</div>}
              </div>
            </div>
            <div className="p-2 flex items-center justify-between gap-2">
              <div className="text-[10px] text-[#555] font-bold">
                👁 {r.views || 0} · ❤ {r.likes || 0}
              </div>
              <button
                data-testid={`admin-delete-reel-${r.id}`}
                onClick={() => doDelete(r)}
                disabled={busyId === r.id}
                className="h-8 px-2 border-hard-2 rounded-lg bg-[#FF9E80] flex items-center gap-1 text-[10px] font-black uppercase press-btn shadow-hard-sm disabled:opacity-50"
              >
                <Trash2 strokeWidth={3} className="w-3 h-3" />
                Sil
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function BroadcastPanel() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/broadcast", { text: text.trim() });
      setResult(data);
      setText("");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-broadcast-panel" className="space-y-4 max-w-2xl">
      <div>
        <div className="font-display text-2xl">Duyuru</div>
        <div className="text-sm text-[#555] font-medium">
          Şu an bağlı olan tüm kullanıcılara ekran üstü mesaj gönderir. Sohbete kaydedilmez.
        </div>
      </div>

      <textarea
        data-testid="admin-broadcast-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={800}
        rows={4}
        placeholder="Duyuru metni…"
        className="w-full bg-white border-hard rounded-2xl px-4 py-3 text-sm focus:outline-none shadow-hard-sm resize-none"
      />
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#555] font-bold">{text.length}/800</div>
        <button
          data-testid="admin-broadcast-send"
          onClick={send}
          disabled={busy || !text.trim()}
          className="h-11 px-5 bg-[#CCFF00] text-[#0A0A0A] font-black border-hard rounded-xl press-btn shadow-hard disabled:opacity-50 flex items-center gap-2"
        >
          <Megaphone strokeWidth={3} className="w-4 h-4" />
          {busy ? "Gönderiliyor…" : "Duyuru gönder"}
        </button>
      </div>

      {err && <div className="border-hard-2 rounded-xl bg-[#FF9E80] p-3 text-sm font-bold">{err}</div>}
      {result && (
        <div className="border-hard-2 rounded-xl bg-[#CCFF00] p-3 text-sm font-bold" data-testid="admin-broadcast-success">
          ✓ Duyuru gönderildi — {result.delivered_sockets} bağlantıya iletildi.
        </div>
      )}
    </section>
  );
}

function MessagesPanel() {
  const [q, setQ] = useState("");
  const [userId, setUserId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (userId.trim()) params.set("user_id", userId.trim());
      if (fromDate) params.set("from_date", new Date(fromDate).toISOString());
      if (toDate)   params.set("to_date",   new Date(toDate).toISOString());
      params.set("limit", "200");
      const { data } = await api.get(`/admin/messages?${params.toString()}`);
      setRows(data.messages || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, userId, fromDate, toDate]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const doDelete = async (m) => {
    if (!window.confirm("Bu mesajı KALICI olarak silmek istiyor musun?")) return;
    setBusyId(m.id);
    try {
      await api.delete(`/admin/messages/${m.id}`);
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `yappo-messages-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section data-testid="admin-messages-panel" className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-display text-2xl">Mesaj denetimi</div>
          <div className="text-sm text-[#555] font-medium">
            {total} mesaj bulundu · Yalnızca hukuki ve moderasyon amaçlı kullanım
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-messages-export"
            onClick={exportJson}
            disabled={!rows.length}
            className="h-10 px-3 border-hard-2 rounded-xl bg-white text-xs font-black uppercase press-btn shadow-hard-sm disabled:opacity-40"
          >
            JSON İndir
          </button>
          <button
            data-testid="admin-messages-refresh"
            onClick={load}
            className="w-10 h-10 border-hard rounded-xl bg-white flex items-center justify-center press-btn shadow-hard-sm"
            aria-label="Yenile"
          >
            <RefreshCw strokeWidth={3} className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="md:col-span-2 flex items-center gap-2 bg-white border-hard rounded-xl px-3 py-2 shadow-hard-sm">
          <Search strokeWidth={2.5} className="w-4 h-4" />
          <input
            data-testid="admin-messages-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Mesaj içeriğinde ara…"
            className="flex-1 bg-transparent focus:outline-none text-sm"
          />
        </div>
        <input
          data-testid="admin-messages-user-id"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="Kullanıcı ID (opsiyonel)"
          className="bg-white border-hard rounded-xl px-3 py-2 shadow-hard-sm text-sm focus:outline-none"
        />
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            data-testid="admin-messages-from"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="flex-1 bg-white border-hard rounded-xl px-2 py-2 shadow-hard-sm text-xs focus:outline-none"
          />
          <span className="text-xs font-black">→</span>
          <input
            type="date"
            data-testid="admin-messages-to"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="flex-1 bg-white border-hard rounded-xl px-2 py-2 shadow-hard-sm text-xs focus:outline-none"
          />
        </div>
      </div>

      {err && <div className="border-hard-2 rounded-xl bg-[#FF9E80] p-3 text-sm font-bold">{err}</div>}

      <div className="space-y-2" data-testid="admin-messages-list">
        {loading && <div className="text-center text-[#555] py-8">Yükleniyor…</div>}
        {!loading && rows.length === 0 && <div className="text-center text-[#555] py-8">Filtreye uyan mesaj yok.</div>}
        {rows.map((m) => (
          <div
            key={m.id}
            data-testid={`admin-message-row-${m.id}`}
            className="border-hard-2 rounded-2xl p-3 shadow-hard-sm bg-white flex items-start gap-3"
          >
            <Avatar name={m.sender?.name} picture={m.sender?.picture} size={36} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-black text-sm truncate">{m.sender?.name || "?"}</div>
                <span className="text-[10px] text-[#555]">·</span>
                <div className="text-[10px] text-[#555] font-mono truncate">{m.sender?.email}</div>
                <span className="text-[10px] text-[#555]">·</span>
                <div className="text-[10px] text-[#555]">{new Date(m.created_at).toLocaleString("tr-TR")}</div>
              </div>
              <div className="text-[10px] uppercase font-black tracking-widest text-[#555] mt-0.5">
                {m.conversation?.is_group ? "GRUP" : "BİREBİR"} · {m.conversation?.name} · {m.content_type}
              </div>
              <div className="mt-1.5 text-sm leading-relaxed break-words whitespace-pre-wrap">
                {m.content_type === "text" && (m.content || <span className="text-[#888]">(boş)</span>)}
                {m.content_type !== "text" && (
                  <>
                    {m.content && <div className="mb-1">{m.content}</div>}
                    {m.media_url && (
                      <a href={m.media_url} target="_blank" rel="noopener noreferrer" className="text-xs underline font-bold text-[#3366cc]">
                        {m.content_type} dosyasını aç ↗
                      </a>
                    )}
                  </>
                )}
              </div>
            </div>
            <button
              data-testid={`admin-delete-message-${m.id}`}
              onClick={() => doDelete(m)}
              disabled={busyId === m.id}
              className="h-8 w-8 border-hard-2 rounded-lg bg-[#FF9E80] flex items-center justify-center press-btn shadow-hard-sm disabled:opacity-50 shrink-0"
              aria-label="Mesajı sil"
              title="Mesajı kalıcı olarak sil"
            >
              <Trash2 strokeWidth={3} className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
